import {
  AdBanner,
  AdvContainer,
  AdvWrapper,
  Layout,
} from "../../../style/Home/RecommendSection.module";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { client } from "../../../client";
import { GET_AD_BANNER } from "../../admin-ad/Ad.gql";
import {
  LogType,
  createClickLog,
  createViewLog,
} from "../../../utils/log/logService";
import styled from "styled-components";

// 직접 구현한 캐러셀을 위한 스타일 컴포넌트
const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
`;

const CarouselTrack = styled.div`
  display: flex;
  transition: transform 0.5s ease-in-out;
  height: 100%;
`;

const CarouselSlide = styled.div`
  min-width: 100%;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.7);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  font-size: 18px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  opacity: 0.8;
  z-index: 10;
  transition: all 0.2s;

  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.9);
  }

  &:focus {
    outline: none;
  }
`;

const PrevButton = styled(CarouselButton)`
  left: 10px;
`;

const NextButton = styled(CarouselButton)`
  right: 10px;
`;

const CarouselIndicators = styled.div`
  position: absolute;
  bottom: 10px;
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 8px;
`;

const Indicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) =>
    props.active ? "#fff" : "rgba(255, 255, 255, 0.5)"};
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.8);
  }
`;

export const MainAdv = () => {
  const ACCOUNT_ID = 10;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselTrackRef = useRef(null);
  const intervalRef = useRef(null);
  const slideRefs = useRef([]);

  useEffect(() => {
    const fetchAds = async () => {
      setLoading(true);
      try {
        const { data } = await client.query({
          query: GET_AD_BANNER,
          variables: {
            visible: true,
          },
          fetchPolicy: "no-cache",
        });

        const tempAds = data.getAdBannerList
          .filter((ad) => ad.account_id === ACCOUNT_ID)
          .map((ad, idx) => {
            return {
              ...ad,
              key: idx,
              log_type: LogType.AD,
            };
          });

        console.log("광고 아이템 로드됨:", tempAds);
        setItems(tempAds);
      } catch (error) {
        console.error("Error fetching ads:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAds();
  }, []);

  // 자동 슬라이드 관련 로직
  useEffect(() => {
    if (items.length <= 1) return;

    const startAutoSlide = () => {
      intervalRef.current = setInterval(() => {
        goToNextSlide();
      }, 5000);
    };

    startAutoSlide();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [items.length, currentIndex]);

  // 인덱스 변경 시 뷰 로그 생성
  useEffect(() => {
    if (items.length > 0 && currentIndex < items.length) {
      createViewLog(items[currentIndex]);
    }
  }, [currentIndex, items]);

  // 사용자가 버튼 클릭 시 자동 슬라이드 재시작
  const resetInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (items.length > 1) {
      intervalRef.current = setInterval(() => {
        goToNextSlide();
      }, 5000);
    }
  }, [items.length]);

  const goToSlide = useCallback(
    (index) => {
      // 인덱스 범위 체크
      let newIndex = index;
      if (index < 0) newIndex = items.length - 1;
      if (index >= items.length) newIndex = 0;

      setCurrentIndex(newIndex);
      resetInterval();

      // 트랙 이동
      if (carouselTrackRef.current) {
        carouselTrackRef.current.style.transform = `translateX(-${
          newIndex * 100
        }%)`;
      }
    },
    [items.length, resetInterval]
  );

  const goToPrevSlide = useCallback(() => {
    goToSlide(currentIndex - 1);
  }, [currentIndex, goToSlide]);

  const goToNextSlide = useCallback(() => {
    goToSlide(currentIndex + 1);
  }, [currentIndex, goToSlide]);

  const handleBannerClick = async (item) => {
    // 클릭 로그 생성
    await createClickLog({
      account_id: ACCOUNT_ID,
      brand: item.url_link,
    });

    // URL 리디렉션 로직
    const url = item.url_link;
    if (url && !url.includes("whisky365")) {
      window.location.href = url;
      return;
    }

    try {
      const currentUrl = new URL(window.location.href);
      const companyId = parseInt(currentUrl.searchParams.get("companyId"), 10);

      const targetPath =
        companyId === 10 ? "/whisky365?companyId=10" : "/whisky365";

      window.location.href = `${window.location.origin}${targetPath}`;
    } catch (error) {
      console.error("Invalid URL:", window.location.href);
      window.location.pathname = "/whisky365";
    }
  };

  // 커스텀 LoggingAdvWrapper 컴포넌트
  const LoggingAdvWrapper = ({ children, item, ...props }) => {
    const wrapperRef = useRef(null);

    // IntersectionObserver를 사용해 요소 가시성 감지
    useEffect(() => {
      if (!wrapperRef.current || !item) return;

      const options = {
        root: null,
        rootMargin: "0px",
        threshold: 0.5,
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && item.key === currentIndex) {
            // 요소가 화면에 보이고 현재 활성화된 배너인 경우
            createViewLog(item);
            observer.disconnect();
          }
        });
      }, options);

      observer.observe(wrapperRef.current);

      return () => {
        if (wrapperRef.current) {
          observer.disconnect();
        }
      };
    }, [item]);

    return (
      <AdvWrapper ref={wrapperRef} {...props}>
        {children}
      </AdvWrapper>
    );
  };

  return (
    <Layout>
      <AdvContainer>
        {loading ? (
          <div
            style={{
              height: "200px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* 로딩 표시자 (필요시 추가) */}
          </div>
        ) : items.length === 0 ? (
          <div
            style={{
              height: "200px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* 아이템이 없을 때 표시 (필요시 추가) */}
          </div>
        ) : (
          <CarouselContainer>
            <CarouselTrack
              ref={carouselTrackRef}
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {items.map((item, i) => (
                <CarouselSlide key={i}>
                  <LoggingAdvWrapper item={item}>
                    <AdBanner
                      src={item.image_url}
                      alt={item.name}
                      onClick={() => handleBannerClick(item)}
                      ref={(el) => (slideRefs.current[i] = el)}
                    />
                  </LoggingAdvWrapper>
                </CarouselSlide>
              ))}
            </CarouselTrack>

            {items.length > 1 && (
              <>
                <PrevButton onClick={goToPrevSlide}>&#10094;</PrevButton>
                <NextButton onClick={goToNextSlide}>&#10095;</NextButton>

                <CarouselIndicators>
                  {items.map((_, i) => (
                    <Indicator
                      key={i}
                      active={i === currentIndex}
                      onClick={() => goToSlide(i)}
                    />
                  ))}
                </CarouselIndicators>
              </>
            )}
          </CarouselContainer>
        )}
      </AdvContainer>
    </Layout>
  );
};
